import React from "react";
import PropTypes from "prop-types";
import Header from "../../components/header";
import MobileHeader from "../../components/MobileHeader";
import { useMediaQuery } from "react-responsive";

export default function TermsAndConditions({
  siteName = "Axionnest",
  url = "http://axionnest.com/",
}) {
  const isMobile = useMediaQuery({ maxWidth: 768 });

  return (
    <>
      {isMobile ? (
        <MobileHeader hideHome={true} hideHamburger={true} hideFilter={true} />
      ) : (
        <Header hideHome={true} hideFilter={true} />
      )}

      <main
        className="max-w-4xl mx-auto my-8 p-6 sm:my-6 sm:p-4 font-sans bg-white leading-relaxed rounded-2xl shadow-sm"
        aria-labelledby="tc-heading"
      >
        {/* Header */}
        <header className="border-b border-gray-200 pb-6 mb-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-[#e8f0f7] rounded-lg flex items-center justify-center">
              <svg className="w-5 h-5 text-[#0f6abf]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
            </div>
            <h1
              id="tc-heading"
              className="text-2xl sm:text-xl font-semibold text-gray-900"
            >
              Terms &amp; Conditions
            </h1>
          </div>

          <p className="text-gray-700">
            Welcome to <strong className="text-[#0f6abf]">{siteName}</strong> ("we", "our", "us"). By
            accessing or using our website{" "}
            <a
              href={`https://${url}`}
              className="text-[#0f6abf] underline hover:no-underline"
            >
              {url}
            </a>{" "}
            (the "Site") and any related services, you agree to be bound by
            these Terms &amp; Conditions ("Terms"). If you do not agree with
            these Terms, please do not use our Site.
          </p>
        </header>

        {/* TOC */}
        <nav
          className="my-6 p-5 bg-[#e8f0f7] border border-gray-200 rounded-xl"
          aria-label="Table of contents"
        >
          <div className="flex items-center gap-2 mb-3">
            <div className="w-1.5 h-4 bg-[#0f6abf] rounded-full"></div>
            <strong className="text-gray-900">On this page</strong>
          </div>
          <ul className="mt-3 ml-4 space-y-2">
            <li><a href="#use" className="text-[#0f6abf] hover:underline">1. Use of Our Site</a></li>
            <li><a href="#ip" className="text-[#0f6abf] hover:underline">2. Intellectual Property Rights</a></li>
            <li><a href="#user-content" className="text-[#0f6abf] hover:underline">3. User Content</a></li>
            <li><a href="#products" className="text-[#0f6abf] hover:underline">4. Product/Service Information</a></li>
            <li><a href="#payment" className="text-[#0f6abf] hover:underline">5. Payment &amp; Refunds</a></li>
            <li><a href="#third-party" className="text-[#0f6abf] hover:underline">6. Third-Party Links</a></li>
            <li><a href="#liability" className="text-[#0f6abf] hover:underline">7. Limitation of Liability</a></li>
            <li><a href="#indemnification" className="text-[#0f6abf] hover:underline">8. Indemnification</a></li>
            <li><a href="#termination" className="text-[#0f6abf] hover:underline">9. Termination</a></li>
            <li><a href="#changes" className="text-[#0f6abf] hover:underline">10. Changes to These Terms</a></li>
            <li><a href="#law" className="text-[#0f6abf] hover:underline">11. Governing Law</a></li>
            <li><a href="#contact" className="text-[#0f6abf] hover:underline">12. Contact Us</a></li>
          </ul>
        </nav>

        {/* Sections */}
        {[
          ["use", "1. Use of Our Site", (
            <ul className="ml-5 space-y-2 list-disc">
              <li className="text-gray-700">You must be at least 18 years old to use our services.</li>
              <li className="text-gray-700">You agree to use the Site for lawful purposes only.</li>
              <li className="text-gray-700">Unauthorized access or scraping is prohibited.</li>
            </ul>
          )],
          ["ip", "2. Intellectual Property Rights", (
            <p className="text-gray-700">All content on the Site is our property or licensed to us.</p>
          )],
          ["user-content", "3. User Content", (
            <p className="text-gray-700">You grant us a worldwide, royalty-free license to use submitted content.</p>
          )],
          ["products", "4. Product/Service Information", (
            <p className="text-gray-700">We do not guarantee product accuracy or availability.</p>
          )],
          ["payment", "5. Payment & Refunds", (
            <p className="text-gray-700">Refunds are subject to our Refund Policy.</p>
          )],
          ["third-party", "6. Third-Party Links", (
            <p className="text-gray-700">We are not responsible for third-party websites.</p>
          )],
          ["liability", "7. Limitation of Liability", (
            <p className="text-gray-700">We are not liable for damages arising from Site usage.</p>
          )],
          ["indemnification", "8. Indemnification", (
            <p className="text-gray-700">You agree to indemnify us from claims resulting from misuse.</p>
          )],
          ["termination", "9. Termination", (
            <p className="text-gray-700">We may terminate access at our discretion.</p>
          )],
          ["changes", "10. Changes to These Terms", (
            <p className="text-gray-700">Terms may be updated at any time.</p>
          )],
          ["law", "11. Governing Law", (
            <p className="text-gray-700">These Terms are governed by your local laws.</p>
          )],
        ].map(([id, title, content]) => (
          <section key={id} id={id} className="my-6 p-4 bg-gray-50 rounded-xl">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-1.5 h-4 bg-[#0f6abf] rounded-full"></div>
              <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
            </div>
            {content}
          </section>
        ))}

        {/* Contact */}
        <section id="contact" className="my-6 p-4 bg-gray-50 rounded-xl">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-1.5 h-4 bg-[#0f6abf] rounded-full"></div>
            <h2 className="text-lg font-semibold text-gray-900">12. Contact Us</h2>
          </div>
          <address className="not-italic text-gray-700">
            <a
              href="mailto:contact@axionnest.com"
              className="text-[#0f6abf] underline hover:no-underline"
            >
              contact@axionnest.com
            </a>
          </address>
        </section>

        {/* Footer */}
        <footer className="mt-8 pt-6 border-t border-gray-200 text-gray-500 text-sm">
          <div className="flex items-center justify-between">
            <span>© {new Date().getFullYear()} {siteName}. All rights reserved.</span>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-[#0f6abf] rounded-full"></div>
              <span>Version 1.0</span>
            </div>
          </div>
        </footer>
      </main>
    </>
  );
}
